import { getVolume } from '../utils/volume';
/**
 * Streaming Audio Player
 * Plays sentences as they arrive from server
 * Maintains playback order and seamless transitions
 */

import type { SentenceStreamResponse, AudioMapping } from '../types/api';
import { CacheManager } from './CacheManager';
import { debugLog, isDebug } from '../utils/logger';

export interface WordHighlightInfo {
  word: string;
  timestamp: number;
  charStart: number;
  charEnd: number;
  sentence: string;
}

export interface PlayerEvents {
  onPlay?: (index: number) => void;
  onPause?: () => void;
  onEnd?: () => void;
  onSentenceStart?: (index: number, sentence: string) => void;
  onSentenceEnd?: (index: number) => void;
  onWordHighlight?: (info: WordHighlightInfo) => void;
  onError?: (error: Error) => void;
  /** Waiting for the next sentence from the server (true) / resumed (false) */
  onBuffering?: (buffering: boolean) => void;
  /**
   * The sentence stream failed after playback started; `nextIndex` is the
   * first sentence that will never arrive. Playback has stopped — the caller
   * may continue the remaining text another way (browser speech).
   */
  onStreamFailed?: (nextIndex: number) => void;
}

/**
 * Speaks one sentence locally (browser speechSynthesis). Used for sentences the
 * server marks `fallback: "browser"` (e.g. the site is over its monthly
 * character limit and the sentence isn't cached). speak() resolves when the
 * sentence ends OR is cancelled via stop().
 */
export interface FallbackSpeaker {
  speak(text: string, speed: number): Promise<void>;
  stop(): void;
}

export type FetchProxyFn = (url: string, responseType: 'blob' | 'json') => Promise<{ ok: boolean; dataUrl?: string; data?: any; error?: string }>;

export class StreamingPlayer {
  private sentences: Map<number, SentenceStreamResponse> = new Map();
  private audioElements: Map<number, HTMLAudioElement> = new Map();
  private mappings: Map<number, AudioMapping> = new Map();
  private currentIndex: number = -1;
  private currentAudio: HTMLAudioElement | null = null;
  private isPlaying: boolean = false;
  private isPaused: boolean = false;
  private speed: number = 1.0;
  private events: PlayerEvents = {};
  private cacheManager: CacheManager;
  private fetchProxy?: FetchProxyFn;
  /** Indices the server reported as failed — skipped during playback */
  private skipped: Set<number> = new Set();
  /** Server sent `event: complete` (or the stream closed) */
  private streamDone: boolean = false;
  private streamFailed: boolean = false;
  private started: boolean = false;
  private buffering: boolean = false;
  /** Bumped by stop()/destroy() so pending waits give up */
  private generation: number = 0;
  /** Sentences to speak with the browser voice (index → text) */
  private fallbacks: Map<number, string> = new Map();
  private speaker?: FallbackSpeaker;
  /** Index of the browser-spoken sentence that is current (playing or paused), else -1 */
  private fallbackIndex: number = -1;
  /** Bumped whenever the current browser utterance is cancelled (pause/stop/seek/speed) */
  private fallbackToken: number = 0;

  constructor(cacheManager: CacheManager, events: PlayerEvents = {}, fetchProxy?: FetchProxyFn, speaker?: FallbackSpeaker) {
    this.cacheManager = cacheManager;
    this.events = events;
    this.fetchProxy = fetchProxy;
    this.speaker = speaker;
  }

  /**
   * Add sentence to queue
   */
  async addSentence(response: SentenceStreamResponse): Promise<void> {
    debugLog(`[StreamingPlayer] addSentence:`, JSON.stringify({ index: response.index, audioUrl: response.audioUrl?.substring(0, 80), mappingUrl: response.mappingUrl?.substring(0, 80), cached: response.cached, error: response.error, fallback: response.fallback }));

    // Server asks us to read this sentence with the browser voice (e.g. over the
    // monthly limit and not cached). Never surfaced to visitors.
    // Also any sentence that failed to generate (provider slow or down): reading it
    // with the browser voice beats skipping it in silence.
    if ((response.fallback === 'browser' || response.error) && typeof response.sentence === 'string' && response.sentence.trim()) {
      if (isDebug()) console.info(`[ReadingVox] Sentence ${response.index}: browser voice (${response.error || 'fallback'})`);
      if (this.speaker) {
        this.fallbacks.set(response.index, response.sentence);
      } else {
        this.skipped.add(response.index); // no speechSynthesis — skip like other errors
      }
      this.maybeAutoStart(response.index);
      return;
    }

    // Server-side error for one sentence: skip it, keep reading the rest
    if (response.error || !response.audioUrl) {
      if (response.error === 'quota_exceeded') {
        if (isDebug()) console.info(`[ReadingVox] Skipping sentence ${response.index}: ${response.error}`);
      } else {
        console.warn(`[ReadingVox] Skipping sentence ${response.index}:`, response.error || 'no audio URL');
      }
      this.skipped.add(response.index);
      this.maybeAutoStart(response.index);
      return;
    }

    this.sentences.set(response.index, response);

    // Preload audio
    await this.preloadAudio(response.index);

    this.maybeAutoStart(response.index);
  }

  /** Start playback automatically when the first sentence (or its skip) arrives */
  private maybeAutoStart(index: number): void {
    if (index === 0 && !this.started && !this.isPlaying && !this.isPaused) {
      this.play();
    }
  }

  /** The server finished the stream (`event: complete` or the stream closed) */
  markStreamComplete(): void {
    this.streamDone = true;
  }

  /** The stream failed mid-way; sentences that never arrived won't be waited for */
  markStreamFailed(): void {
    this.streamFailed = true;
    this.streamDone = true;
  }

  /** Whether a sentence has word-level timings (English voices do; others highlight per sentence) */
  hasWordTimings(index: number): boolean {
    return this.mappings.has(index);
  }

  /** Number of sentences received (with audio, browser-voice, or skipped) so far */
  receivedCount(): number {
    return this.sentences.size + this.fallbacks.size + this.skipped.size;
  }

  /** Text of a received sentence (used to find where a fallback should resume) */
  getSentenceText(index: number): string | null {
    return this.sentences.get(index)?.sentence ?? this.fallbacks.get(index) ?? null;
  }

  private setBuffering(buffering: boolean): void {
    if (this.buffering === buffering) return;
    this.buffering = buffering;
    this.events.onBuffering?.(buffering);
  }

  /**
   * Preload audio for a sentence
   */
  private async preloadAudio(index: number): Promise<void> {
    const sentence = this.sentences.get(index);
    if (!sentence) return;

    // Cache key includes the audioUrl which is voice-specific (server cache key has voice hash)
    const cacheKey = `audio-${sentence.audioUrl}`;
    const cached = await this.cacheManager.get(cacheKey);

    let audioUrl: string;

    if (cached) {
      debugLog(`[StreamingPlayer] Sentence ${index}: using cached audio`);
      audioUrl = URL.createObjectURL(cached.audioBlob);
    } else if (this.fetchProxy) {
      // Route through extension service worker to avoid CORS/PNA issues
      try {
        debugLog(`[StreamingPlayer] Sentence ${index}: proxy-fetching audio from`, sentence.audioUrl);
        const result = await this.fetchProxy(sentence.audioUrl, 'blob');
        debugLog(`[StreamingPlayer] Sentence ${index}: proxy result ok=${result.ok}, hasDataUrl=${!!result.dataUrl}, error=${result.error || 'none'}`);
        if (result.ok && result.dataUrl) {
          // Convert data URL to blob URL — data URLs can fail for large audio
          const resp = await fetch(result.dataUrl);
          const blob = await resp.blob();
          audioUrl = URL.createObjectURL(blob);
        } else {
          console.error('Proxy fetch audio failed:', result.error);
          audioUrl = sentence.audioUrl;
        }
      } catch (error) {
        console.error('Failed to proxy-fetch audio:', error);
        audioUrl = sentence.audioUrl;
      }
    } else {
      // Direct fetch (same-origin or non-extension context)
      try {
        const response = await fetch(sentence.audioUrl);
        const blob = await response.blob();
        await this.cacheManager.save(cacheKey, blob, sentence.mappingUrl, sentence.duration);
        audioUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.error('Failed to fetch/cache audio:', error);
        audioUrl = sentence.audioUrl;
      }
    }

    // Create audio element
    debugLog(`[StreamingPlayer] Sentence ${index}: creating Audio element, url type=${audioUrl.startsWith('data:') ? 'dataUrl' : audioUrl.startsWith('blob:') ? 'blobUrl' : 'remote'}, length=${audioUrl.length}`);
    const audio = new Audio(audioUrl);
    audio.playbackRate = this.speed;
    audio.volume = getVolume();
    audio.preload = 'auto';

    this.audioElements.set(index, audio);

    // Load word mapping if available
    if (sentence.mappingUrl) {
      try {
        let mapping: AudioMapping;
        if (this.fetchProxy) {
          const result = await this.fetchProxy(sentence.mappingUrl, 'json');
          if (result.ok && result.data) {
            mapping = result.data;
          } else {
            throw new Error(result.error || 'Proxy fetch mapping failed');
          }
        } else {
          const mappingResponse = await fetch(sentence.mappingUrl);
          mapping = await mappingResponse.json();
        }
        this.mappings.set(index, mapping);
      } catch (error) {
        console.error('Failed to load word mapping:', error);
      }
    }
  }

  /**
   * Play from current position
   */
  async play(): Promise<void> {
    if (this.isPlaying && !this.isPaused) return;

    if (this.isPaused && this.fallbackIndex >= 0 && this.fallbackIndex === this.currentIndex) {
      // Resume a browser-spoken sentence: speechSynthesis pause/resume is
      // unreliable (Chrome), so the sentence is spoken again from its start
      this.isPaused = false;
      this.isPlaying = true;
      this.started = true;
      this.speakFallback(this.currentIndex);
      return;
    }

    if (this.isPaused && this.currentAudio) {
      // Resume current audio
      this.currentAudio.play();
      this.isPaused = false;
      this.isPlaying = true;
      this.events.onPlay?.(this.currentIndex);
      return;
    }

    // Start playing from current index
    this.isPaused = false;
    this.isPlaying = true;
    this.started = true;
    await this.playNext();
  }

  /**
   * Play next sentence in queue
   */
  private async playNext(): Promise<void> {
    if (!this.isPlaying) return;

    const gen = this.generation;
    let nextIndex = this.currentIndex + 1;
    let waitedMs = 0;

    // Find the next playable sentence: skip failed ones, wait (buffering)
    // while the stream is still open, end only once the server is done.
    while (!this.sentences.has(nextIndex) && !this.fallbacks.has(nextIndex)) {
      if (this.skipped.has(nextIndex)) { nextIndex++; continue; }

      if (this.streamDone) {
        const later = [...this.sentences.keys(), ...this.fallbacks.keys(), ...this.skipped].some((i) => i > nextIndex);
        if (later) { nextIndex++; continue; } // never sent — skip the gap
        this.setBuffering(false);
        this.isPlaying = false;
        if (this.streamFailed) {
          this.events.onStreamFailed?.(nextIndex);
        } else {
          this.events.onEnd?.();
        }
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 100));
      if (gen !== this.generation || !this.isPlaying) return; // stopped / paused meanwhile
      waitedMs += 100;
      if (waitedMs >= 400) this.setBuffering(true);
    }
    this.setBuffering(false);

    this.currentIndex = nextIndex;

    if (this.fallbacks.has(nextIndex)) {
      this.currentAudio = null;
      this.speakFallback(nextIndex);
      return;
    }

    // Wait for audio to be preloaded
    await this.waitForAudio(nextIndex);

    const audio = this.audioElements.get(nextIndex);

    if (!audio) {
      console.error('Audio not found for index:', nextIndex);
      await this.playNext(); // Skip to next
      return;
    }

    this.currentAudio = audio;

    // Event handlers
    audio.onended = () => {
      this.events.onSentenceEnd?.(nextIndex);
      this.playNext(); // Auto-play next sentence
    };

    audio.onerror = (error) => {
      console.error('Audio playback error:', error);
      this.events.onError?.(new Error('Audio playback failed'));
      this.playNext(); // Skip to next
    };

    // Word highlighting (if mapping available)
    if (this.mappings.has(nextIndex)) {
      this.setupWordHighlighting(audio, nextIndex);
    }

    // Start playback
    debugLog(`[StreamingPlayer] Playing sentence ${nextIndex}, audio src type=${audio.src.startsWith('data:') ? 'dataUrl' : audio.src.startsWith('blob:') ? 'blobUrl' : 'remote'}, duration=${audio.duration}, readyState=${audio.readyState}`);
    this.events.onSentenceStart?.(nextIndex, this.sentences.get(nextIndex)!.sentence);
    try {
      await audio.play();
      debugLog(`[StreamingPlayer] Sentence ${nextIndex} playing successfully`);
    } catch (error) {
      console.error('[StreamingPlayer] Audio play failed:', error);
      this.events.onError?.(new Error(`Audio playback failed for sentence ${nextIndex}`));
      await this.playNext();
      return;
    }
    this.events.onPlay?.(nextIndex);
  }

  /**
   * Speak sentence `index` with the browser voice, then continue with the next
   * sentence. Superseded utterances (pause/stop/seek/speed change) are ignored.
   * Word highlighting comes from the speaker's own boundary events.
   */
  private async speakFallback(index: number): Promise<void> {
    const text = this.fallbacks.get(index);
    if (!text || !this.speaker) return;
    const token = ++this.fallbackToken;
    const gen = this.generation;
    this.fallbackIndex = index;
    this.currentAudio = null;

    this.events.onSentenceStart?.(index, text);
    this.events.onPlay?.(index);
    try {
      await this.speaker.speak(text, this.speed);
    } catch (error) {
      debugLog('[StreamingPlayer] browser voice failed for sentence', index, error);
    }
    if (token !== this.fallbackToken || gen !== this.generation || !this.isPlaying) return;

    this.fallbackIndex = -1;
    this.events.onSentenceEnd?.(index);
    this.playNext();
  }

  /** Cancel the current browser utterance (if any) without advancing */
  private cancelFallbackSpeech(): void {
    this.fallbackToken++;
    try { this.speaker?.stop(); } catch { /* ignore */ }
  }

  /**
   * Setup word highlighting for sentence
   */
  private setupWordHighlighting(audio: HTMLAudioElement, index: number): void {
    const mapping = this.mappings.get(index);
    if (!mapping) return;

    const sentence = this.sentences.get(index);
    if (!sentence) return;

    let currentWordIndex = 0;
    let lastHighlightedIndex = -1;

    const updateHighlight = () => {
      if (!this.isPlaying || this.currentIndex !== index) return;

      const currentTime = audio.currentTime;
      const words = mapping.words;

      if (words.length === 0) return;

      // Advance to the most recently passed word:
      // If we've passed the start of the next word, move forward
      while (currentWordIndex < words.length - 1) {
        if (currentTime >= words[currentWordIndex + 1].start) {
          currentWordIndex++;
        } else {
          break;
        }
      }

      // Only emit highlight if the word actually changed
      if (currentWordIndex !== lastHighlightedIndex && currentTime >= words[currentWordIndex].start) {
        this.events.onWordHighlight?.({
          word: words[currentWordIndex].text,
          timestamp: currentTime,
          charStart: words[currentWordIndex].charStart,
          charEnd: words[currentWordIndex].charEnd,
          sentence: sentence.sentence
        });
        lastHighlightedIndex = currentWordIndex;
      }

      // Continue updating
      if (currentTime < audio.duration) {
        requestAnimationFrame(updateHighlight);
      }
    };

    // Start highlighting updates
    requestAnimationFrame(updateHighlight);
  }

  /**
   * Pause playback
   */
  pause(): void {
    if (!this.isPlaying || this.isPaused) return;

    if (this.fallbackIndex >= 0 && this.fallbackIndex === this.currentIndex) {
      // Browser-spoken sentence: cancel it; play() speaks it again
      this.cancelFallbackSpeech();
    } else if (this.buffering) {
      // Paused while waiting for the next sentence — resume via playNext()
      this.setBuffering(false);
      this.currentAudio = null;
    } else if (this.currentAudio) {
      this.currentAudio.pause();
    }

    this.isPaused = true;
    this.isPlaying = false;
    this.events.onPause?.();
  }

  /**
   * Stop playback
   */
  stop(): void {
    if (this.fallbackIndex >= 0) {
      this.cancelFallbackSpeech();
      this.fallbackIndex = -1;
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    }

    this.isPlaying = false;
    this.isPaused = false;
    this.currentIndex = -1;
    this.currentAudio = null;
    this.generation++;
    this.setBuffering(false);
  }

  /**
   * Set playback speed
   */
  /** Apply the current volume to loaded audio (new audio picks it up on creation) */
  applyVolume(): void {
    const v = getVolume();
    this.audioElements.forEach(a => { a.volume = v; });
    if (this.currentAudio) this.currentAudio.volume = v;
  }

  setSpeed(speed: number): void {
    this.speed = Math.max(0.5, Math.min(2.0, speed));

    // Update current audio if playing
    if (this.currentAudio) {
      this.currentAudio.playbackRate = this.speed;
    }

    // Update all preloaded audio
    for (const audio of this.audioElements.values()) {
      audio.playbackRate = this.speed;
    }

    // Browser speech rate is fixed per utterance — restart the current sentence
    if (this.fallbackIndex >= 0 && this.fallbackIndex === this.currentIndex && this.isPlaying && !this.isPaused) {
      this.cancelFallbackSpeech();
      this.speakFallback(this.currentIndex);
    }
  }

  /**
   * Wait for audio to be preloaded
   */
  private async waitForAudio(index: number): Promise<void> {
    const timeout = 10000; // 10 seconds
    const start = Date.now();

    while (!this.audioElements.has(index) && Date.now() - start < timeout) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /** Rough duration of a browser-spoken sentence (seconds) */
  private estimateFallbackDuration(text: string): number {
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    return words / 2.5 / Math.max(0.5, this.speed);
  }

  /** All received sentences in order with (estimated) durations */
  private timeline(): { index: number; duration: number; start: number; fallback: boolean }[] {
    const entries: { index: number; duration: number; start: number; fallback: boolean }[] = [];
    for (const [index, sentence] of this.sentences) {
      const audio = this.audioElements.get(index);
      entries.push({ index, duration: audio?.duration || sentence.duration || 0, start: 0, fallback: false });
    }
    for (const [index, text] of this.fallbacks) {
      entries.push({ index, duration: this.estimateFallbackDuration(text), start: 0, fallback: true });
    }
    entries.sort((a, b) => a.index - b.index);
    let total = 0;
    for (const e of entries) { e.start = total; total += e.duration; }
    return entries;
  }

  /**
   * Jump `delta` sentences forward (+) or back (−) from the current one.
   * Goes straight to the target sentence (no percentage math), so sentences
   * whose audio hasn't loaded yet (duration unknown) can't throw it off.
   */
  skip(delta: number): void {
    const indices = [...new Set([...this.sentences.keys(), ...this.fallbacks.keys()])].sort((a, b) => a - b);
    if (indices.length === 0) return;
    const pos = indices.indexOf(this.currentIndex);
    const target = indices[Math.max(0, Math.min(indices.length - 1, (pos < 0 ? 0 : pos) + delta))];
    if (target === this.currentIndex && pos >= 0) return;

    // Stop what's playing now
    if (this.fallbackIndex >= 0) {
      this.cancelFallbackSpeech();
      this.fallbackIndex = -1;
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.onended = null;
      this.currentAudio = null;
    }
    const target_audio = this.audioElements.get(target);
    if (target_audio) target_audio.currentTime = 0;

    // playNext() advances to currentIndex + 1 (loading/buffering as needed)
    this.currentIndex = target - 1;
    if (this.isPlaying && !this.isPaused) {
      void this.playNext();
    } else if (this.fallbacks.has(target)) {
      this.currentIndex = target;
      this.fallbackIndex = target;
      this.events.onSentenceStart?.(target, this.fallbacks.get(target)!);
    } else if (this.sentences.has(target)) {
      // Paused: show where playback will resume
      this.events.onSentenceStart?.(target, this.sentences.get(target)!.sentence);
    }
  }

  /**
   * Seek to a percentage of total duration (0-100)
   */
  seek(percent: number): void {
    const clampedPercent = Math.max(0, Math.min(100, percent));

    // Calculate total duration across all loaded sentences (browser-voice ones estimated)
    const sentenceDurations = this.timeline();
    const totalDuration = sentenceDurations.reduce((sum, e) => sum + e.duration, 0);

    if (totalDuration === 0) return;

    // Map percentage to absolute time
    const targetTime = (clampedPercent / 100) * totalDuration;

    // Find which sentence contains the target time
    let targetSentence = sentenceDurations[0];
    for (const entry of sentenceDurations) {
      if (targetTime >= entry.start && targetTime < entry.start + entry.duration) {
        targetSentence = entry;
        break;
      }
      // If past all sentences, use the last one
      targetSentence = entry;
    }

    const offsetWithinSentence = targetTime - targetSentence.start;

    // Stop current audio / browser speech
    if (this.fallbackIndex >= 0) {
      this.cancelFallbackSpeech();
      this.fallbackIndex = -1;
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.onended = null;
    }

    if (targetSentence.fallback) {
      // Browser-voice sentence: always starts from its beginning
      this.currentIndex = targetSentence.index;
      this.currentAudio = null;
      if (this.isPlaying) {
        this.speakFallback(targetSentence.index);
      } else {
        // Paused: play() resumes by speaking this sentence
        this.fallbackIndex = targetSentence.index;
        this.events.onSentenceStart?.(targetSentence.index, this.fallbacks.get(targetSentence.index)!);
      }
      return;
    }

    // Jump to target sentence
    this.currentIndex = targetSentence.index - 1; // playNext increments
    const audio = this.audioElements.get(targetSentence.index);
    if (audio) {
      audio.currentTime = Math.min(offsetWithinSentence, audio.duration || 0);
      this.currentAudio = audio;
      this.currentIndex = targetSentence.index;

      // Set up onended to continue to next sentence
      audio.onended = () => {
        this.events.onSentenceEnd?.(targetSentence.index);
        this.playNext();
      };

      // Re-setup word highlighting
      if (this.mappings.has(targetSentence.index)) {
        this.setupWordHighlighting(audio, targetSentence.index);
      }

      if (this.isPlaying) {
        audio.play().catch(err => {
          console.error('[ReadingVox] Seek play failed:', err);
        });
      }

      this.events.onSentenceStart?.(
        targetSentence.index,
        this.sentences.get(targetSentence.index)!.sentence
      );
    }
  }

  /**
   * Get playback progress as a percentage (0-100)
   */
  getProgress(): number {
    let totalDuration = 0;
    let elapsedBefore = 0;

    for (const entry of this.timeline()) {
      if (entry.index < this.currentIndex) {
        elapsedBefore += entry.duration;
      } else if (entry.index === this.currentIndex && !entry.fallback && this.currentAudio) {
        elapsedBefore += this.currentAudio.currentTime;
      }
      totalDuration += entry.duration;
    }

    if (totalDuration === 0) return 0;
    return (elapsedBefore / totalDuration) * 100;
  }

  /**
   * Check if playing
   */
  isActive(): boolean {
    return this.isPlaying;
  }

  /**
   * Check if paused
   */
  isPausedState(): boolean {
    return this.isPaused;
  }

  /**
   * Get current sentence index
   */
  getCurrentIndex(): number {
    return this.currentIndex;
  }

  /**
   * Clean up
   */
  destroy(): void {
    this.stop();

    // Revoke object URLs
    for (const audio of this.audioElements.values()) {
      audio.src = '';
    }

    this.sentences.clear();
    this.audioElements.clear();
    this.mappings.clear();
    this.skipped.clear();
    this.fallbacks.clear();
  }
}
